import React, { useState, useEffect, useCallback } from "react";
import {
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  Edit2,
  X,
  Eye,
  EyeOff,
  AlertTriangle,
  Clock,
  Shield,
  Loader2,
  Terminal,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "react-toastify";
import { generateApiKey, fetchApiKeys, updateApiKey, revokeApiKey } from "../apis/apiKeys";

const ApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [showQuickStart, setShowQuickStart] = useState(false);

  const [formName, setFormName] = useState("");
  const [formRateLimit, setFormRateLimit] = useState(100);

  const loadKeys = useCallback(async () => {
    try {
      const res = await fetchApiKeys();
      if (res.success) setKeys(res.keys || []);
    } catch {
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleGenerate = async () => {
    if (!formName.trim()) { toast.error("Key name is required"); return; }
    setGenerating(true);
    try {
      const res = await generateApiKey({ name: formName.trim(), rate_limit: formRateLimit });
      if (res.success) {
        setNewKeyResult(res);
        setShowCreateModal(false);
        setFormName("");
        setFormRateLimit(100);
        loadKeys();
      } else {
        toast.error(res.error || "Failed to generate key");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to generate key");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async (id) => {
    try {
      const res = await revokeApiKey(id);
      if (res.success) {
        toast.success("API key revoked");
        setConfirmRevoke(null);
        loadKeys();
      }
    } catch {
      toast.error("Failed to revoke key");
    }
  };

  const handleUpdateKey = async () => {
    if (!editingKey) return;
    try {
      const res = await updateApiKey(editingKey.id, { name: editingKey.name, rate_limit: editingKey.rate_limit });
      if (res.success) {
        toast.success("Key updated");
        setEditingKey(null);
        loadKeys();
      }
    } catch {
      toast.error("Failed to update key");
    }
  };

  const API_BASE =
    process.env.REACT_APP_PUBLIC_API_URL || "https://apis-ping.blutec.ai";

  return (
    <div className="min-h-screen bg-gray-50/50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Key size={20} className="text-[#048AAD]" /> API Keys
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage your Public API keys for programmatic access to Ping.</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#048AAD] text-white text-sm font-medium rounded-lg hover:bg-[#037291] transition-colors"
          >
            <Plus size={15} /> Generate Key
          </button>
        </div>

        {/* New key result banner */}
        {newKeyResult && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Check size={18} className="text-green-600" />
                <span className="text-sm font-semibold text-green-800">API Key Created</span>
              </div>
              <button onClick={() => setNewKeyResult(null)} className="text-green-400 hover:text-green-600"><X size={16} /></button>
            </div>
            <p className="text-xs text-green-700">Copy this key now. It will <strong>not be shown again</strong>.</p>
            <div className="flex items-center gap-2 bg-white border border-green-200 rounded-lg px-3 py-2">
              <code className="flex-1 text-xs font-mono text-gray-800 break-all select-all">{newKeyResult.api_key}</code>
              <button
                onClick={() => handleCopy(newKeyResult.api_key)}
                className="shrink-0 p-1.5 rounded-md hover:bg-green-100 text-green-600 transition-colors"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        )}

        {/* Quick start */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setShowQuickStart(!showQuickStart)}
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Terminal size={16} className="text-[#048AAD]" />
              <span className="text-sm font-semibold text-gray-700">Quick Start Guide</span>
            </div>
            {showQuickStart ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>

          {showQuickStart && (
            <div className="px-5 pb-4 border-t border-gray-100 pt-3 space-y-3">
              <p className="text-xs text-gray-500">Send your first message using the Ping Public API:</p>

              <div className="bg-gray-900 rounded-lg p-4 text-xs font-mono text-green-400 overflow-x-auto whitespace-pre">
{`curl -X POST ${API_BASE}/api/v1/messages/text \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone_number": "YOUR_WA_BUSINESS_NUMBER",
    "to": "RECIPIENT_NUMBER",
    "message": "Hello from Ping API!"
  }'`}
              </div>

              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="font-semibold text-gray-700 mb-1">Base URL</div>
                  <code className="text-[#048AAD]">{API_BASE}/api/v1</code>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="font-semibold text-gray-700 mb-1">Authentication</div>
                  <code className="text-[#048AAD]">X-API-Key</code> header
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="font-semibold text-gray-700 mb-1">Rate Limit</div>
                  <span className="text-gray-600">100 req/min (default)</span>
                </div>
              </div>

              <div className="text-xs text-gray-500 space-y-1">
                <p className="font-semibold text-gray-700">Available Endpoints:</p>
                <div className="grid grid-cols-2 gap-1">
                  <code className="bg-gray-100 px-2 py-0.5 rounded">POST /api/v1/messages/text</code>
                  <code className="bg-gray-100 px-2 py-0.5 rounded">POST /api/v1/messages/media</code>
                  <code className="bg-gray-100 px-2 py-0.5 rounded">POST /api/v1/messages/template</code>
                  <code className="bg-gray-100 px-2 py-0.5 rounded">POST /api/v1/media/upload</code>
                  <code className="bg-gray-100 px-2 py-0.5 rounded">GET  /api/v1/templates</code>
                  <code className="bg-gray-100 px-2 py-0.5 rounded">GET  /api/v1/flows</code>
                  <code className="bg-gray-100 px-2 py-0.5 rounded">GET  /api/v1/flows/:flowId/variables</code>
                  <code className="bg-gray-100 px-2 py-0.5 rounded">POST /api/v1/flows/:flowId/activate</code>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Keys table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-700">Your API Keys</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 size={20} className="animate-spin mr-2" /> Loading...
            </div>
          ) : keys.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              No API keys yet. Click "Generate Key" to create one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-5 py-2.5 text-left font-medium">Name</th>
                    <th className="px-5 py-2.5 text-left font-medium">Key</th>
                    <th className="px-5 py-2.5 text-left font-medium">Status</th>
                    <th className="px-5 py-2.5 text-left font-medium">Rate Limit</th>
                    <th className="px-5 py-2.5 text-left font-medium">Last Used</th>
                    <th className="px-5 py-2.5 text-left font-medium">Created</th>
                    <th className="px-5 py-2.5 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {keys.map((k) => (
                    <tr key={k.id} className={`hover:bg-gray-50 ${!k.is_active ? "opacity-50" : ""}`}>
                      <td className="px-5 py-3 font-medium text-gray-800">{k.name}</td>
                      <td className="px-5 py-3">
                        <code className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">
                          {k.key_prefix}...
                        </code>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          k.is_active ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"
                        }`}>
                          {k.is_active ? "Active" : "Revoked"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-gray-500">{k.rate_limit}/min</td>
                      <td className="px-5 py-3 text-gray-400">
                        {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : "Never"}
                      </td>
                      <td className="px-5 py-3 text-gray-400">
                        {new Date(k.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {k.is_active && (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setEditingKey({ ...k })}
                              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                              title="Edit"
                            >
                              <Edit2 size={13} />
                            </button>
                            <button
                              onClick={() => setConfirmRevoke(k.id)}
                              className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500"
                              title="Revoke"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Security note */}
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <Shield size={18} className="text-amber-500 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800 space-y-1">
            <p className="font-semibold">Security Best Practices</p>
            <ul className="list-disc list-inside space-y-0.5 text-amber-700">
              <li>Never share your API keys publicly or commit them to version control.</li>
              <li>Use environment variables to store keys in your applications.</li>
              <li>Revoke and regenerate keys if you suspect they have been compromised.</li>
              <li>Set IP whitelisting for production keys to restrict access.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800">Generate New API Key</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Key Name *</label>
                <input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Production Server, My App"
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#048AAD]/30 focus:border-[#048AAD]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Rate Limit (requests/minute)</label>
                <input
                  type="number"
                  value={formRateLimit}
                  onChange={(e) => setFormRateLimit(parseInt(e.target.value) || 100)}
                  min={1}
                  max={1000}
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#048AAD]/30 focus:border-[#048AAD]"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#048AAD] rounded-lg hover:bg-[#037291] disabled:opacity-50"
              >
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editingKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEditingKey(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800">Edit API Key</h3>
              <button onClick={() => setEditingKey(null)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Key Name</label>
                <input
                  value={editingKey.name}
                  onChange={(e) => setEditingKey({ ...editingKey, name: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#048AAD]/30 focus:border-[#048AAD]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Rate Limit (requests/minute)</label>
                <input
                  type="number"
                  value={editingKey.rate_limit}
                  onChange={(e) => setEditingKey({ ...editingKey, rate_limit: parseInt(e.target.value) || 100 })}
                  min={1}
                  max={1000}
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#048AAD]/30 focus:border-[#048AAD]"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditingKey(null)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleUpdateKey} className="px-4 py-2 text-sm font-medium text-white bg-[#048AAD] rounded-lg hover:bg-[#037291]">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke confirmation */}
      {confirmRevoke && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setConfirmRevoke(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={18} />
              <h3 className="text-sm font-bold">Revoke API Key?</h3>
            </div>
            <p className="text-xs text-gray-600">This action cannot be undone. Any applications using this key will immediately lose access.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmRevoke(null)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleRevoke(confirmRevoke)} className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600">Revoke</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeys;
